// src/modules/app-views/fiscalConfig/pages/Document/tabs/NfceTab/modals/VoidNfceModal/index.jsx
// *******************************************************************************
// VOID NFCe MODAL - Inutilização de numeração NFC-e
// *******************************************************************************
// Modal for voiding (inutilização) NFC-e number ranges with SEFAZ
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { ConfigProvider } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';

import GenericModal from 'components/core/GenericModal';
import { darkTheme, lightTheme } from 'configs/appEngine';
import { API } from 'constants/Api';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import translateX from 'utils/translateX';

import voidNfceModalStep from './Step1';

// *******************************************************************************
// HELPERS
// *******************************************************************************
const extractChaveFromXml = xmlStr => {
  if (!xmlStr) return null;
  // retInutNFe XML has <infInut Id="ID{digits}"> — extract digits after "ID"
  const match = xmlStr.match(/Id="ID(\d+)"/);
  return match?.[1] || null;
};

// *******************************************************************************
// THEME WRAPPER
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const VoidNfceModal = ({ show, setShow, afterClose, company, onSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [resultData, setResultData] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [importingNumbers, setImportingNumbers] = useState({});

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleImport = useCallback(
    async (numero, force = false) => {
      const companyId = company?._id || company?.entityId;
      const serie = company?.fiscalData?.nfce?.currentSerie || 1;

      setImportingNumbers(prev => ({ ...prev, [numero]: 'loading' }));

      try {
        const res = await axiosService.post({
          url: API.FISCAL.NFCE_IMPORT,
          data: { companyId, serie, numero, ...(force && { force: true }) },
        });
        const data = Array.isArray(res) ? res[0] : res;

        if (data?.success === true) {
          setResultData(prev => ({
            ...prev,
            rangeCheck: {
              // Replace existing entry for this numero (handles reimport of incomplete records)
              found: [
                ...(prev.rangeCheck?.found || []).filter(inv => inv.numero !== numero),
                data.invoice,
              ],
              missing: (prev.rangeCheck?.missing || []).filter(n => n !== numero),
            },
          }));
          setImportingNumbers(prev => {
            const next = { ...prev };
            delete next[numero];
            return next;
          });
        } else {
          setImportingNumbers(prev => ({ ...prev, [numero]: 'error' }));
        }
      } catch {
        setImportingNumbers(prev => ({ ...prev, [numero]: 'error' }));
      }
    },
    [company]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // MEMOS
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(
    () => [
      voidNfceModalStep({
        company,
        resultData,
        showResult,
        onImport: handleImport,
        importingNumbers,
      }),
    ],
    [company, resultData, showResult, handleImport, importingNumbers]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form, setLoading }) => {
    // If showing result, just close the modal
    if (showResult) {
      return true;
    }

    const values = form?.getFieldsValue(true) || {};

    const companyId = company?._id || company?.entityId;
    const companySerie = company?.fiscalData?.nfce?.currentSerie || 1;

    // Simplified payload - backend fetches cnpj and ambiente from companyId
    const payload = {
      companyId,
      serie: companySerie,
      numero_inicial: values.numero_inicial,
      numero_final: values.numero_final,
      justificativa: values.justificativa,
    };

    setLoading(true);

    try {
      const response = await axiosService.post({
        url: API.FISCAL.NFCE_VOID,
        data: payload,
      });

      // Handle axiosService response format (can be array or object)
      let responseData;
      if (Array.isArray(response)) {
        // axiosService returns [data, summary] when returnAsObject is used
        responseData = response[0];
      } else {
        responseData = response;
      }

      // Check success at root level (routerService wraps response)
      const isSuccess = responseData?.success === true;
      const hasData = responseData?.data?.success === true;

      if (isSuccess || hasData) {
        // Get data from either root or data property
        const successData = hasData ? responseData.data : responseData;

        // Store result data for display
        const result = {
          success: true,
          protocolNumber: successData.protocolNumber || successData.results?.[0]?.protocolNumber,
          operationId: successData.operationId,
          processed: successData.processed,
          results: successData.results,
          xml: successData.xml,
          xmlStorageUrl: successData.xmlStorageUrl,
          range: successData.results?.[0] || { start: values.numero_inicial, end: values.numero_final },
        };

        setResultData(result);
        setShowResult(true);

        // Callback for parent component
        onSuccess?.(result);

        // Don't close modal - show result instead
        return false;
      } else {
        // Get error details from response
        const errorDetails = responseData?.raw || responseData;

        // Build meaningful error message
        let errorMsg = translateX('void_nfce_failed');
        let errorCode = null;

        // Check for specific SEFAZ errors
        if (responseData?.errors?.[0]?.error) {
          errorMsg = responseData.errors[0].error;
          errorCode = responseData.errors[0].code;
        } else if (errorDetails?.motivo_status) {
          // NuvemFiscal rejection
          errorMsg = errorDetails.motivo_status;
          errorCode = errorDetails.codigo_status;
        } else if (errorDetails?.errors?.[0]?.message) {
          errorMsg = errorDetails.errors[0].message;
        } else if (errorDetails?.error?.message) {
          errorMsg = errorDetails.error.message;
        } else if (responseData?.message) {
          errorMsg = responseData.message;
        } else if (responseData?.error) {
          errorMsg = responseData.error;
        }

        // Store error data for display in error view
        const errorResult = {
          success: false,
          error: errorMsg,
          code: errorCode,
          operationId: responseData?.operationId,
          chave: responseData?.raw?.chave || extractChaveFromXml(responseData?.xml),
          xml: responseData?.xml,
          xmlStorageUrl: responseData?.xmlStorageUrl,
          range: { start: values.numero_inicial, end: values.numero_final },
        };

        if (Number(errorCode) === 241) {
          try {
            const rangeRes = await axiosService.post({
              url: API.FISCAL.NFCE_CHECK_RANGE,
              data: {
                companyId,
                serie: companySerie,
                numero_inicial: values.numero_inicial,
                numero_final: values.numero_final,
              },
            });
            const rangeData = Array.isArray(rangeRes) ? rangeRes[0] : rangeRes;
            setResultData({ ...errorResult, code: 241, rangeCheck: rangeData });
          } catch {
            setResultData(errorResult);
          }
        } else {
          setResultData(errorResult);
        }

        setShowResult(true);
        return false;
      }
    } catch (error) {
      console.error('[VoidNfceModal] Error:', error);

      const errorMsg =
        error?.response?.data?.errors?.[0]?.error ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        translateX('void_nfce_failed');

      appService.message({
        flag: 'e',
        text: errorMsg,
        duration: 5,
      });

      return false;
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={showResult ? 'void_nfce_result_title' : 'void_nfce_title'}
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={560}
      maskClosable={false}
      nextButtonText={showResult ? 'close' : 'void_numbers'}
    />
  );
};

// *******************************************************************************
// SHOW MODAL FUNCTION
// *******************************************************************************
const showVoidNfceModal = ({ company, onSuccess } = {}) => {
  const container = document.createElement('div');
  container.id = 'void-nfce-modal-container';
  document.body.appendChild(container);

  const root = createRoot(container);
  let isModalOpen = true;

  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <VoidNfceModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          company={company}
          onSuccess={onSuccess}
        />
      </ThemeWrapper>
    );
  };

  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 0);
  };

  renderModal();
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default showVoidNfceModal;
export { VoidNfceModal };
